import React, { useCallback, useMemo, useRef, useState } from "react";
import { IoSchool } from "react-icons/io5";
import { CopyOutlined, FilePdfOutlined } from "@ant-design/icons";
import { message } from "antd";
import HandlingFeePicker from "../../../../Components/Agent/PaymentAgent/HandlingFeePicker/HandlingFeePicker";
import {
  generateTourMarketingText,
  copyMarketingTextToClipboard,
} from "../../../../utils/marketingTextUtils";
import { useOrganizedTourQuoteCommission } from "../../../../modules/organizedTours/context/OrganizedTourQuoteCommissionContext";
import OrganizedTourSmartQuoteActions from "../../../../modules/organizedTours/components/OrganizedTourSmartQuoteActions";
import { getUserInfo } from "../../../../services/localStorage";
import {
  ORGANIZED_TOUR_QUOTE_TERMS_FALLBACK_HE,
  buildAgentBrandingFromUser,
  buildOrganizedTourQuoteSnapshotPayload,
  computeOrganizedTourUiQuotePricing,
  postOrganizedTourQuoteSnapshotToServer,
} from "../../../../modules/organizedTours/utils/organizedTourQuoteSnapshotPayload";
import { downloadOrganizedTourProfileOfferPdf } from "../../../../modules/organizedTours/utils/downloadOrganizedTourLdsOfferPdf";
import { tokenToPublicOfferUrl } from "../../../../modules/organizedTours/utils/organizedTourPublicQuoteUrl";
import { get_depret_from_result, parseToLdsDate } from "../../../../utils/LDSHelpers";
import { useOrganizedTourDisplayedQuotePricing } from "../../../../modules/organizedTours/hooks/useOrganizedTourDisplayedQuotePricing";

export default function OrganizedTourProfileAgentPanel({
  t,
  airlines,
  handleFeeData,
  updateHandleFeeData,
  orderId,
  createCustomerPriceQuote,
  ourPrice,
  ourSavedLDSOrder,
  selectedResult,
  roomsAndPaxInfo,
  agentData,
  itineraryQuoteSourceText,
  resolvedDealId,
}) {
  const [busyCopyPublicLink, setBusyCopyPublicLink] = useState(false);
  const [busyQuotePdf, setBusyQuotePdf] = useState(false);
  const quoteOfferTokenRef = useRef(null);

  const quoteCommission = useOrganizedTourQuoteCommission();
  const quotePricing = useOrganizedTourDisplayedQuotePricing({
    selectedResult,
    ourPrice,
    orgTourQuoteOverride: quoteCommission?.override ?? null,
  });
  const customerPriceIls = useMemo(() => {
    const quote = computeOrganizedTourUiQuotePricing(quotePricing);
    const n = Number(quote?.finalQuotePrice);
    return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
  }, [quotePricing]);

  const itineraryForSnapshot = String(itineraryQuoteSourceText || "");

  const resolveQuoteCustomerContact = useCallback(() => {
    const user = getUserInfo() || {};
    const branding = buildAgentBrandingFromUser(user);
    const lead = roomsAndPaxInfo?.[0]?.adults?.[0] || {};
    const leadName = [lead.firstName || lead.FirstName, lead.lastName || lead.LastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    const customerName =
      String(ourSavedLDSOrder?.customerName || "").trim() ||
      leadName ||
      "לקוח (טרם צוין שם)";
    const customerPhone =
      String(
        ourSavedLDSOrder?.phone ||
          ourSavedLDSOrder?.customerPhone ||
          ourSavedLDSOrder?.mobile ||
          ""
      ).trim() || "0000000000";
    const customerEmail = String(ourSavedLDSOrder?.email || "").trim() || null;
    return { branding, customerName, customerPhone, customerEmail };
  }, [ourSavedLDSOrder, roomsAndPaxInfo]);

  const buildPanelQuoteSnapshot = useCallback(() => {
    const { branding, customerName, customerPhone, customerEmail } = resolveQuoteCustomerContact();
    const built = buildOrganizedTourQuoteSnapshotPayload({
      ...quotePricing,
      itinerarySourceText: itineraryForSnapshot,
      termsSourceText: ORGANIZED_TOUR_QUOTE_TERMS_FALLBACK_HE,
      customerName,
      customerPhone,
      customerEmail,
      branding,
      dealId: resolvedDealId,
      roomsAndPaxInfo,
    });
    return { ...built, customerName, customerPhone };
  }, [
    itineraryForSnapshot,
    quotePricing,
    resolvedDealId,
    resolveQuoteCustomerContact,
    roomsAndPaxInfo,
  ]);

  const assertSessionOrToast = useCallback(() => {
    const user = getUserInfo() || {};
    const sid = user.sessionID || user.sessionId;
    if (!sid) {
      message.error("יש להתחבר מחדש כדי ליצור קישור הצעה");
      return null;
    }
    return { user, sid };
  }, []);

  const copyTextToClipboardSafe = useCallback(async (text) => {
    const t = String(text || "");
    try {
      await navigator.clipboard.writeText(t);
      return true;
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = t;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        return Boolean(ok);
      } catch {
        return false;
      }
    }
  }, []);

  const handleCopyPublicQuoteLink = useCallback(async () => {
    // eslint-disable-next-line no-console
    console.log("[ORG_TOUR_QUOTE_COPY_LINK_CLICK]", { offer_type: "organized_tour" });
    const sess = assertSessionOrToast();
    if (!sess) {
      // eslint-disable-next-line no-console
      console.log("[ORG_TOUR_QUOTE_LINK_COPY_ERROR]", { reason: "no_session" });
      return;
    }
    setBusyCopyPublicLink(true);
    try {
      const { payload, finalQuotePrice, customerName, customerPhone } = buildPanelQuoteSnapshot();
      const res = await postOrganizedTourQuoteSnapshotToServer(payload, finalQuotePrice);
      // eslint-disable-next-line no-console
      console.log("[ORG_TOUR_QUOTE_LINK_RESOLVE]", {
        ok: Boolean(res?.success),
        hasToken: Boolean(res?.token),
      });
      if (!res?.success || !res?.token) {
        const msg = res?.error?.message || "יצירת קישור ציבורי נכשלה";
        throw new Error(msg);
      }
      const url = tokenToPublicOfferUrl(res.token);
      if (!url) {
        throw new Error("לא התקבל קישור תקף מהשרת");
      }
      quoteOfferTokenRef.current = String(res.token);
      const copied = await copyTextToClipboardSafe(url);
      if (!copied) {
        throw new Error("העתקה ללוח נכשלה — נסו שוב או העתיקו ידנית: " + url);
      }
      // eslint-disable-next-line no-console
      console.log("[ORG_TOUR_QUOTE_LINK_COPY_SUCCESS]", { offer_type: "organized_tour" });
      message.success("הקישור הועתק ללוח — ניתן לשלוח ללקוח");
    } catch (err) {
      const msg =
        err?.message ||
        err?.response?.data?.error?.message ||
        "שגיאה בהעתקת קישור — נסו שוב או השתמשו ב«הצעת מחיר חכמה»";
      // eslint-disable-next-line no-console
      console.log("[ORG_TOUR_QUOTE_LINK_COPY_ERROR]", { message: msg });
      message.error(msg);
    } finally {
      setBusyCopyPublicLink(false);
    }
  }, [
    assertSessionOrToast,
    buildPanelQuoteSnapshot,
    copyTextToClipboardSafe,
  ]);

  const handleProduceQuotePdf = useCallback(async () => {
    const sess = assertSessionOrToast();
    if (!sess) {
      // eslint-disable-next-line no-console
      console.log("[ORG_TOUR_PDF_ERROR]", { reason: "no_session" });
      return;
    }

    setBusyQuotePdf(true);
    try {
      const resolvedOrderId =
        orderId || ourSavedLDSOrder?.id || ourSavedLDSOrder?.ourLdsId || null;

      const quote = computeOrganizedTourUiQuotePricing(quotePricing);

      const { payload } = buildPanelQuoteSnapshot();
      const { depDate, retDate } = selectedResult ? get_depret_from_result(selectedResult) : {};
      const quotePdfPostBody = {
        sessionID: sess.sid,
        agent_id: sess.user?.id ?? sess.user?.agentId ?? null,
        ...payload,
        supplier_id: selectedResult?.supplierDetails?.supplierId,
        dep_date: depDate != null ? parseToLdsDate(depDate) : "",
        ret_date: retDate != null ? parseToLdsDate(retDate) : "",
        price_ils: quote.finalQuotePrice,
      };

      await downloadOrganizedTourProfileOfferPdf({
        ldsOrderId: resolvedOrderId,
        totalPriceIls: quote.finalQuotePrice,
        quotePdfPostBody: resolvedOrderId ? null : quotePdfPostBody,
      });
      message.success("הקובץ ירד — פתחו אותו מההורדות");
    } catch (err) {
      const msg =
        err?.message ||
        err?.response?.data?.error?.message ||
        "הפקת PDF נכשלה — בדקו חיבור ונסו שוב";
      message.error(msg);
    } finally {
      setBusyQuotePdf(false);
    }
  }, [
    assertSessionOrToast,
    buildPanelQuoteSnapshot,
    orderId,
    ourSavedLDSOrder,
    quotePricing,
    selectedResult,
  ]);

  const copyMarketingText = useCallback(async () => {
    const quote = computeOrganizedTourUiQuotePricing(quotePricing);

    try {
      const user = getUserInfo() || {};
      const sid = user.sessionID || user.sessionId;
      if (sid) {
        const branding = buildAgentBrandingFromUser(user);
        const lead = roomsAndPaxInfo?.[0]?.adults?.[0] || {};
        const leadName = [
          lead.firstName || lead.FirstName,
          lead.lastName || lead.LastName,
        ]
          .filter(Boolean)
          .join(" ")
          .trim();
        const { payload, finalQuotePrice } = buildOrganizedTourQuoteSnapshotPayload({
          ...quotePricing,
          itinerarySourceText: itineraryForSnapshot,
          customerName:
            String(ourSavedLDSOrder?.customerName || "").trim() ||
            leadName ||
            "מלל שיווקי (לא צוין שם)",
          customerPhone:
            String(
              ourSavedLDSOrder?.phone ||
                ourSavedLDSOrder?.customerPhone ||
                ourSavedLDSOrder?.mobile ||
                ""
            ).trim() || "0000000000",
          customerEmail: String(ourSavedLDSOrder?.email || "").trim() || null,
          branding,
          dealId: resolvedDealId,
          roomsAndPaxInfo,
        });
        const res = await postOrganizedTourQuoteSnapshotToServer(payload, finalQuotePrice);
        if (import.meta.env?.DEV && typeof console !== "undefined") {
          // eslint-disable-next-line no-console
          console.log("[ORG_TOUR_MARKETING_COPY_SNAPSHOT]", {
            ok: Boolean(res?.success),
          });
        }
      }
    } catch {
      message.warning(
        "המלל הועתק; שמירת גרסת שרת לפי המחיר הנוכחי בתור הצעה ציבורית נכשלה"
      );
    }

    const marketingText = generateTourMarketingText({
      selectedResult,
      roomsAndPaxInfo,
      ourPrice,
      ourSavedLDSOrder,
      agentData,
      airlines,
      t,
      displayPriceIls: quote.finalQuotePrice,
    });
    copyMarketingTextToClipboard(marketingText, message);
  }, [
    agentData,
    airlines,
    itineraryForSnapshot,
    ourPrice,
    ourSavedLDSOrder,
    quotePricing,
    resolvedDealId,
    roomsAndPaxInfo,
    selectedResult,
    t,
  ]);

  return (
    <section className="otp-agent-panel" dir="rtl" aria-label="ניהול הצעה">
      <header className="otp-agent-panel__header">
        <div className="otp-agent-panel__title-row">
          <IoSchool aria-hidden className="otp-agent-panel__header-icon" />
          <h2 className="otp-agent-panel__title">ניהול הצעה</h2>
        </div>
        <p className="otp-agent-panel__subtitle">עמלה, דמי טיפול ושיתוף הצעה</p>
      </header>
      <div className="otp-agent-panel__body">
        <HandlingFeePicker
          className="handling-fee-picker--otp-rail"
          extendHandleFeeDefault
          handleFeeData={handleFeeData}
          updateHandleFeeData={updateHandleFeeData}
          orderId={orderId}
          createCustomerPriceQuote={createCustomerPriceQuote}
          ourPrice={ourPrice}
          customerPriceIls={customerPriceIls}
          applyHandleFeeToPriceImmediately
          fixedFeeStoredCurrency="ILS"
          persistFixedPlusAsIls
        />
        <div className="otp-agent-panel__actions">
          {ourSavedLDSOrder ? (
            <button
              type="button"
              className="otp-agent-panel__btn otp-agent-panel__btn--save"
              onClick={() => createCustomerPriceQuote({ moveNextStateFlag: false })}
            >
              שמור הצעת מחיר
            </button>
          ) : null}
          <div className="otp-agent-panel__action-rows">
            <div className="otp-agent-panel__action-row">
              <button
                type="button"
                className="otp-agent-panel__btn otp-agent-panel__action-tile"
                disabled={busyCopyPublicLink || busyQuotePdf}
                onClick={() => handleCopyPublicQuoteLink()}
              >
                {busyCopyPublicLink ? "מעתיק…" : "העתק קישור"}
              </button>
              <button
                type="button"
                className="otp-agent-panel__btn otp-agent-panel__btn--secondary otp-agent-panel__action-tile"
                disabled={busyQuotePdf || busyCopyPublicLink}
                onClick={() => handleProduceQuotePdf()}
              >
                <FilePdfOutlined aria-hidden />
                {busyQuotePdf ? "מכין PDF…" : "יצירת PDF"}
              </button>
            </div>
            <div className="otp-agent-panel__action-row">
              {quoteCommission ? (
                <OrganizedTourSmartQuoteActions
                  selectedResult={selectedResult}
                  ourPrice={ourPrice}
                  orgTourQuoteOverride={quoteCommission.override ?? null}
                  itineraryQuoteSourceText={itineraryQuoteSourceText}
                  dealId={resolvedDealId}
                  ldsOrderId={orderId || ourSavedLDSOrder?.id || ourSavedLDSOrder?.ourLdsId || null}
                  roomsAndPaxInfo={roomsAndPaxInfo}
                  triggerClassName="otp-agent-panel__action-tile"
                />
              ) : null}
              <button
                type="button"
                className={
                  quoteCommission
                    ? "otp-agent-panel__btn otp-agent-panel__btn--ghost otp-agent-panel__action-tile"
                    : "otp-agent-panel__btn otp-agent-panel__btn--ghost otp-agent-panel__action-tile otp-agent-panel__action-tile--span"
                }
                onClick={copyMarketingText}
              >
                <CopyOutlined aria-hidden />
                העתק מלל שיווקי
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
